import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ordersApi } from '@/lib/api/orders';
import { inventoryApi } from '@/lib/api/inventory';
import { locationsApi } from '@/lib/api/locations';
import { formatToEST } from '@/lib/utils/date-format';
import { useDialog } from '@/hooks/useDialog';
import { Dialog } from '@/components/ui/dialog';
import { InventoryItemModal } from '@/features/inventory/components/inventory-item-modal';
import type { OrderDetail, OrderStatus, PickEvent, OrderItem } from '@/types/order';
import type { InventoryItem, Location } from '@/types/inventory';

interface SortableOrderRowProps {
  item: OrderItem;
  index: number;
  isOpen: boolean;
  isEditable: boolean;
  onViewItem: (id: number) => void;
  onReassign: (id: number) => void;
  onRemove: (item: OrderItem) => void;
  isPreparingReassignment: boolean;
}

function SortableOrderRow({ item, index, isOpen, isEditable, onViewItem, onReassign, onRemove, isPreparingReassignment }: SortableOrderRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !isOpen });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b border-gray-700 ${item.isPicked ? 'bg-green-900 bg-opacity-20' : 'hover:bg-gray-750'}`}
    >
      {isOpen && (
        <td
          {...attributes}
          {...listeners}
          className="px-2 py-3 text-center text-gray-500 cursor-move"
        >
          <span className="text-lg">⋮⋮</span>
        </td>
      )}
      <td className="px-4 py-3 text-sm">{index + 1}</td>
      <td className="px-4 py-3 font-mono">
        <button
          onClick={() => onViewItem(item.inventoryItemId)}
          className="text-blue-400 hover:text-blue-300 hover:underline"
        >
          {item.barcodeValue}
        </button>
      </td>
      <td className="px-4 py-3">{item.locationCode || '-'}</td>
      <td className="px-4 py-3">{item.sortId1 || '-'}</td>
      <td className="px-4 py-3">{item.sortId2 || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {item.locationAssignedAt
          ? new Date(item.locationAssignedAt).toLocaleString()
          : '-'}
      </td>
      <td className="px-4 py-3">
        {item.isPicked ? (
          <span className="text-green-400 text-sm">✓ Picked</span>
        ) : (
          <span className="text-gray-500 text-sm">Pending</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm">
        {item.pickedByName || '-'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {!item.isPicked && (
            <button
              onClick={() => onReassign(item.id)}
              disabled={isPreparingReassignment}
              data-testid={`reassign-item-${item.id}`}
              className="text-yellow-400 hover:text-yellow-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPreparingReassignment ? 'Loading...' : 'Reassign'}
            </button>
          )}
          {isEditable && !item.isPicked && (
            <button
              onClick={() => onRemove(item)}
              data-testid={`remove-item-${item.id}`}
              className="text-red-400 hover:text-red-300 text-sm"
            >
              Remove
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { dialog, closeDialog, showSuccess, showError, showConfirm } = useDialog();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [pickEvents, setPickEvents] = useState<PickEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // Single explicit mode flag for the shared inventory-picker modal. The modal is
  // open when this is non-null; the submit handler branches on it so Add and Reassign
  // never leak into each other. `reassigningItemId` is only meaningful in 'reassign' mode.
  const [pickerMode, setPickerMode] = useState<'reassign' | 'add' | null>(null);
  const [reassigningItemId, setReassigningItemId] = useState<number | null>(null);
  const [replacementBarcode, setReplacementBarcode] = useState('');
  const [selectedReplacementId, setSelectedReplacementId] = useState<number | null>(null);
  const [selectedInventoryItemId, setSelectedInventoryItemId] = useState<number | null>(null);
  const [availableItems, setAvailableItems] = useState<InventoryItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [searchBarcode, setSearchBarcode] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterSortId1, setFilterSortId1] = useState('');
  const [filterSortId2, setFilterSortId2] = useState('');
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isPreparingReassignment, setIsPreparingReassignment] = useState(false);
  const isInitialLoadRef = useRef(true);
  const [hasReordered, setHasReordered] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ orderNumber: '', truckNumber: '', truckName: '', customerName: '', productName: '', comments: '' });
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editingTimestamp, setEditingTimestamp] = useState<string | null>(null);
  const [timestampValue, setTimestampValue] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const loadOrder = useCallback(async () => {
    try {
      const data = await ordersApi.getById(parseInt(id!));
      setOrder(data ?? null);
    } catch (error) {
      console.error('Error loading order:', error);
      // If order not found (404), redirect to orders list
      if ((error as any)?.response?.status === 404) {
        navigate('/orders');
        return;
      }
      showError('Error', 'Failed to load order');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, showError]);

  const loadPickEvents = useCallback(async () => {
    try {
      const data = await ordersApi.getPickEvents(parseInt(id!));
      setPickEvents(data ?? []);
    } catch (error) {
      console.error('Error loading pick events:', error);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadOrder();
      loadPickEvents();
    }
  }, [id, loadOrder, loadPickEvents]);

  useEffect(() => {
    if (!autoRefresh || !id) return;

    const interval = setInterval(() => {
      loadOrder();
      loadPickEvents();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, id, loadOrder, loadPickEvents]);

  const handleUnlock = async () => {
    showConfirm(
      'Unlock Order',
      'Are you sure you want to unlock this order?',
      async () => {
        try {
          await ordersApi.unlock(parseInt(id!));
          loadOrder();
          showSuccess('Success', 'Order unlocked successfully');
        } catch (error) {
          console.error('Error unlocking order:', error);
          showError('Error', 'Failed to unlock order');
        }
      },
      'Unlock'
    );
  };

  const handleDelete = async () => {
    showConfirm(
      'Delete Order',
      'Are you sure you want to delete this order? This action cannot be undone.',
      async () => {
        try {
          await ordersApi.delete(parseInt(id!));
          showSuccess('Success', 'Order deleted successfully', () => navigate('/orders'));
        } catch (error) {
          const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to delete order';
          showError('Error', message);
        }
      },
      'Delete'
    );
  };

  const loadAvailableItems = useCallback(async () => {
    try {
      setIsLoadingItems(true);
      const data = await inventoryApi.getInventory({
        pageNumber: 1,
        pageSize: 100,
        status: 'Active',
        locationId: filterLocation ? parseInt(filterLocation) : undefined,
        sortId1: filterSortId1 || undefined,
        sortId2: filterSortId2 || undefined,
      });
      setAvailableItems(data?.items ?? []);
    } catch (error) {
      console.error('Error loading available items:', error);
    } finally {
      setIsLoadingItems(false);
    }
  }, [filterLocation, filterSortId1, filterSortId2]);

  const loadLocations = async (): Promise<Location[]> => {
    try {
      const data = await locationsApi.getAll();
      const loadedLocations = data ?? [];
      setLocations(loadedLocations);
      return loadedLocations;
    } catch (error) {
      console.error('Error loading locations:', error);
      return [];
    }
  };

  // Reload items when user manually changes filters (after modal is already open)
  // Skip on initial load using the ref
  useEffect(() => {
    if (pickerMode && !isPreparingReassignment && !isInitialLoadRef.current) {
      loadAvailableItems();
    }
  }, [filterLocation, filterSortId1, filterSortId2, pickerMode, isPreparingReassignment, loadAvailableItems]);

  const handleReassignItem = async (itemId: number) => {
    setIsPreparingReassignment(true);
    setReplacementBarcode('');
    setSearchBarcode('');
    isInitialLoadRef.current = true;

    try {
      // Load locations first and get the returned data
      let currentLocations = locations;
      if (locations.length === 0) {
        currentLocations = await loadLocations();
      }

      // Find the item and set filters
      const itemToReassign = order?.items.find(item => item.id === itemId);
      if (itemToReassign) {
        const locationId = currentLocations.find(loc => loc.code === itemToReassign.locationCode)?.id.toString() || '';
        setFilterLocation(locationId);
        setFilterSortId1('');
        setFilterSortId2('');

        // Load items filtered by location only — sort IDs are usually item-specific so
        // pre-filtering by them would return no results. The user can filter manually.
        const data = await inventoryApi.getInventory({
          pageNumber: 1,
          pageSize: 100,
          status: 'Active',
          locationId: locationId ? parseInt(locationId) : undefined,
        });
        setAvailableItems(data?.items ?? []);
      }

      // Now show the modal with data already loaded
      setSelectedReplacementId(null);
      setReassigningItemId(itemId);
      setPickerMode('reassign');
      // After initial load is done, mark it as false so useEffect can handle subsequent changes
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    } catch (error) {
      console.error('Error preparing reassignment:', error);
      showError('Error', 'Failed to load available items');
    } finally {
      setIsPreparingReassignment(false);
    }
  };

  const handleAddItem = async () => {
    setIsPreparingReassignment(true);
    setReplacementBarcode('');
    setSearchBarcode('');
    isInitialLoadRef.current = true;

    try {
      if (locations.length === 0) {
        await loadLocations();
      }

      // Add mode starts with no location pre-filter — the whole active pool is a
      // candidate, unlike reassign which pre-filters to the original item's location.
      setFilterLocation('');
      setFilterSortId1('');
      setFilterSortId2('');
      const data = await inventoryApi.getInventory({
        pageNumber: 1,
        pageSize: 100,
        status: 'Active',
      });
      setAvailableItems(data?.items ?? []);

      // Ensure no stale reassign target or prior selection leaks into add mode.
      setSelectedReplacementId(null);
      setReassigningItemId(null);
      setPickerMode('add');
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    } catch (error) {
      console.error('Error preparing add item:', error);
      showError('Error', 'Failed to load available items');
    } finally {
      setIsPreparingReassignment(false);
    }
  };

  // Resets ALL shared picker state so neither mode carries state into the other.
  const handleClosePicker = () => {
    setPickerMode(null);
    setReassigningItemId(null);
    setReplacementBarcode('');
    setSelectedReplacementId(null);
    setAvailableItems([]);
    setSearchBarcode('');
    setFilterLocation('');
    setFilterSortId1('');
    setFilterSortId2('');
  };

  const handleSelectItem = (itemId: number, barcode: string) => {
    setSelectedReplacementId(itemId);
    setReplacementBarcode(barcode);
  };

  const handleSubmitPicker = async () => {
    if (!selectedReplacementId) return;

    try {
      if (pickerMode === 'add') {
        await ordersApi.addItem(parseInt(id!), selectedReplacementId);
        showSuccess('Success', `Item added: ID ${selectedReplacementId} - ${replacementBarcode}`);
      } else if (pickerMode === 'reassign') {
        if (!reassigningItemId) return;
        await ordersApi.reassignItem(parseInt(id!), reassigningItemId, selectedReplacementId);
        showSuccess('Success', `Item reassigned to ID ${selectedReplacementId} - ${replacementBarcode}`);
      } else {
        return;
      }

      handleClosePicker();
      loadOrder();
      loadPickEvents();
    } catch (error) {
      const fallback = pickerMode === 'add' ? 'Failed to add item' : 'Failed to reassign item';
      const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message || fallback;
      showError('Error', message);
    }
  };

  const handleRemoveItem = (item: OrderItem) => {
    showConfirm(
      'Remove Item',
      `Remove item ${item.barcodeValue} from this order? Its inventory will be released.`,
      async () => {
        try {
          await ordersApi.removeItem(parseInt(id!), item.id);
          showSuccess('Success', `Item ${item.barcodeValue} removed from order`);
          loadOrder();
          loadPickEvents();
        } catch (error) {
          const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to remove item';
          showError('Error', message);
        }
      },
      'Remove'
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && order) {
      const oldIndex = order.items.findIndex((item) => item.id === active.id);
      const newIndex = order.items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(order.items, oldIndex, newIndex);
      setOrder({ ...order, items: newItems });
      setHasReordered(true);
    }
  };

  const handleSaveOrder = async () => {
    if (!order || !hasReordered) return;

    try {
      setIsSavingOrder(true);
      const orderedItemIds = order.items.map(item => item.id);
      await ordersApi.reorderItems(parseInt(id!), orderedItemIds);
      showSuccess('Success', 'Order items reordered successfully');
      setHasReordered(false);
      loadOrder();
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to reorder items';
      showError('Error', message);
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleStartEdit = () => {
    if (!order) return;
    setEditData({
      orderNumber: order.orderNumber ?? '',
      truckNumber: order.truckNumber,
      truckName: order.truckName || '',
      customerName: order.customerName,
      productName: order.productName || '',
      comments: order.comments || '',
    });
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!order) return;
    try {
      setIsSavingEdit(true);
      await ordersApi.update(parseInt(id!), {
        // Omit a blank release number rather than sending '' — clearing a set
        // OrderNumber back to blank is a non-goal, and '' would collide on the
        // filtered unique index (which treats '' as a real, non-null value).
        orderNumber: editData.orderNumber || undefined,
        truckNumber: editData.truckNumber,
        truckName: editData.truckName,
        customerName: editData.customerName,
        productName: editData.productName || undefined,
        comments: editData.comments || undefined,
      });
      setIsEditing(false);
      loadOrder();
      showSuccess('Success', 'Order updated successfully');
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to update order';
      showError('Error', message);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleHold = () => {
    showConfirm('Hold Order', 'Put this order on hold?', async () => {
      try {
        await ordersApi.hold(parseInt(id!));
        loadOrder();
        showSuccess('Success', 'Order put on hold');
      } catch (error) {
        showError('Error', 'Failed to hold order');
      }
    }, 'Hold');
  };

  const handleResume = () => {
    showConfirm('Resume Order', 'Resume this order?', async () => {
      try {
        await ordersApi.resume(parseInt(id!));
        loadOrder();
        showSuccess('Success', 'Order resumed');
      } catch (error) {
        showError('Error', 'Failed to resume order');
      }
    }, 'Resume');
  };

  const handleEditTimestamp = (field: string, currentValue?: string) => {
    setEditingTimestamp(field);
    setTimestampValue(currentValue ? new Date(currentValue).toISOString().slice(0, 19) : '');
  };

  const handleSaveTimestamp = async () => {
    if (!editingTimestamp || !order) return;
    try {
      if (editingTimestamp === 'pickedAt' && editingTimestamp) {
        // Item-level timestamp handled separately
        return;
      }
      const data: { createdAt?: string; completedAt?: string; claimedAt?: string } = {};
      if (editingTimestamp === 'createdAt') data.createdAt = new Date(timestampValue).toISOString();
      if (editingTimestamp === 'completedAt') data.completedAt = new Date(timestampValue).toISOString();
      if (editingTimestamp === 'claimedAt') data.claimedAt = new Date(timestampValue).toISOString();
      await ordersApi.updateTimestamps(parseInt(id!), data);
      setEditingTimestamp(null);
      loadOrder();
      showSuccess('Success', 'Timestamp updated');
    } catch (error) {
      showError('Error', 'Failed to update timestamp');
    }
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'Open':
        return 'bg-blue-900 text-blue-200';
      case 'InProgress':
        return 'bg-yellow-900 text-yellow-200';
      case 'Complete':
        return 'bg-green-900 text-green-200';
      case 'Cancelled':
        return 'bg-red-900 text-red-200';
      case 'OnHold':
        return 'bg-orange-900 text-orange-200';
      default:
        return 'bg-gray-700 text-gray-400';
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!order) {
    return (
      <div className="text-center py-8">
        <p className="text-red-400 mb-4">Order not found</p>
        <button
          onClick={() => navigate('/orders')}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded"
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const progressPercentage = order.totalItems > 0 ? (order.pickedItems / order.totalItems) * 100 : 0;
  // Add/Remove item affordances are only allowed while the order is Open or OnHold.
  const isItemEditable = order.status === 'Open' || order.status === 'OnHold';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate('/orders')}
            className="text-blue-400 hover:text-blue-300 mb-2 text-sm"
          >
            ← Back to Orders
          </button>
          <h1 className="text-3xl font-bold">Release {order.orderNumber ?? '—'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center text-sm text-gray-300">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="mr-2"
            />
            Auto-refresh (10s)
          </label>
        </div>
      </div>

      {/* Order Info Card */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Order Info</h2>
          {!isEditing && order.status !== 'Complete' && order.status !== 'Cancelled' && (
            <button onClick={handleStartEdit} className="text-sm text-blue-400 hover:text-blue-300">
              Edit
            </button>
          )}
        </div>
        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Release Number</label>
                <input type="text" value={editData.orderNumber} onChange={e => setEditData({ ...editData, orderNumber: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Truck Number</label>
                <input type="text" value={editData.truckNumber} onChange={e => setEditData({ ...editData, truckNumber: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Truck Name</label>
                <input type="text" value={editData.truckName} onChange={e => setEditData({ ...editData, truckName: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Customer</label>
                <input type="text" value={editData.customerName} onChange={e => setEditData({ ...editData, customerName: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Product Name</label>
                <input type="text" value={editData.productName} onChange={e => setEditData({ ...editData, productName: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Comments</label>
              <textarea value={editData.comments} onChange={e => setEditData({ ...editData, comments: e.target.value })} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500" rows={2} maxLength={2000} />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm">Cancel</button>
              <button onClick={handleSaveEdit} disabled={isSavingEdit} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50">{isSavingEdit ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Release Number</label>
              <p className="text-lg font-semibold">{order.orderNumber ?? '—'}</p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Truck Number</label>
              <p className="text-lg">{order.truckNumber}</p>
            </div>
            {order.truckName && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Truck Name</label>
                <p className="text-lg">{order.truckName}</p>
              </div>
            )}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Customer</label>
              <p className="text-lg">{order.customerName}</p>
            </div>
            {order.productName && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Product Name</label>
                <p className="text-lg">{order.productName}</p>
              </div>
            )}
            {order.isOpenOrder && order.expectedItemCount != null && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Expected Item Count</label>
                <p className="text-lg">{order.pickedItems} / {order.expectedItemCount}</p>
              </div>
            )}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Status</label>
              <span className={`inline-block px-3 py-1 text-sm rounded ${getStatusColor(order.status)}`}>
                {order.status === 'OnHold' ? 'On Hold' : order.status}
              </span>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Progress</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-green-500 h-full transition-all"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <span className="text-sm font-semibold whitespace-nowrap">
                  {order.pickedItems}/{order.totalItems}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Claimed By</label>
              <p className="text-lg">
                {order.claimedByName ? (
                  <span className="text-yellow-400">{order.claimedByName}</span>
                ) : (
                  <span className="text-gray-500">Not claimed</span>
                )}
              </p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Created</label>
              <div className="flex items-center gap-2">
                {editingTimestamp === 'createdAt' ? (
                  <>
                    <input type="datetime-local" step="1" value={timestampValue} onChange={e => setTimestampValue(e.target.value)} className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm" />
                    <button onClick={handleSaveTimestamp} className="text-green-400 text-xs">Save</button>
                    <button onClick={() => setEditingTimestamp(null)} className="text-gray-400 text-xs">Cancel</button>
                  </>
                ) : (
                  <>
                    <p className="text-sm">{formatToEST(order.createdAt)}</p>
                    <button onClick={() => handleEditTimestamp('createdAt', order.createdAt)} className="text-blue-400 text-xs hover:text-blue-300">Edit</button>
                  </>
                )}
              </div>
            </div>
            {order.completedAt && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Completed</label>
                <div className="flex items-center gap-2">
                  {editingTimestamp === 'completedAt' ? (
                    <>
                      <input type="datetime-local" step="1" value={timestampValue} onChange={e => setTimestampValue(e.target.value)} className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm" />
                      <button onClick={handleSaveTimestamp} className="text-green-400 text-xs">Save</button>
                      <button onClick={() => setEditingTimestamp(null)} className="text-gray-400 text-xs">Cancel</button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm">{formatToEST(order.completedAt)}</p>
                      <button onClick={() => handleEditTimestamp('completedAt', order.completedAt)} className="text-blue-400 text-xs hover:text-blue-300">Edit</button>
                    </>
                  )}
                </div>
              </div>
            )}
            {order.comments && (
              <div className="lg:col-span-4">
                <label className="block text-sm text-gray-400 mb-1">Comments</label>
                <p className="text-sm text-gray-300">{order.comments}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {(order.status === 'Open' || order.status === 'InProgress') && (
          <button
            onClick={handleHold}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded transition-colors"
          >
            Hold Order
          </button>
        )}
        {order.status === 'OnHold' && (
          <button
            onClick={handleResume}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded transition-colors"
          >
            Resume Order
          </button>
        )}
        {order.claimedById && order.status !== 'Complete' && (
          <button
            onClick={handleUnlock}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded transition-colors"
          >
            Unlock Order
          </button>
        )}
        {order.status === 'Open' && (
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded transition-colors"
          >
            Delete Order
          </button>
        )}
      </div>

      {/* Order Items */}
      <div className="bg-gray-800 rounded-lg overflow-hidden overflow-x-hidden">
        <div className="px-6 py-3 bg-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Order Items ({order.items.length})</h2>
          <div className="flex items-center gap-2">
            {order.status === 'Open' && hasReordered && (
              <button
                onClick={handleSaveOrder}
                disabled={isSavingOrder}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingOrder ? 'Saving...' : 'Save Order'}
              </button>
            )}
            {isItemEditable && (
              <button
                onClick={handleAddItem}
                disabled={isPreparingReassignment}
                data-testid="add-item-button"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPreparingReassignment ? 'Loading...' : '+ Add Item'}
              </button>
            )}
          </div>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                {order.status === 'Open' && (
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-300 uppercase w-12">

                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Seq
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Barcode
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Sort ID 1
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Sort ID 2
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Assigned At
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Picked By
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <SortableContext
                items={order.items.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {order.items.map((item, index) => (
                  <SortableOrderRow
                    key={item.id}
                    item={item}
                    index={index}
                    isOpen={order.status === 'Open'}
                    isEditable={isItemEditable}
                    onViewItem={setSelectedInventoryItemId}
                    onReassign={handleReassignItem}
                    onRemove={handleRemoveItem}
                    isPreparingReassignment={isPreparingReassignment}
                  />
                ))}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      </div>

      {/* Events */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="px-6 py-3 bg-gray-700">
          <h2 className="text-lg font-semibold">Events ({pickEvents.length})</h2>
        </div>
        {pickEvents.length > 0 ? (
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Barcode
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Operator
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Event Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {pickEvents.map((event) => (
                <tr key={event.id} className="hover:bg-gray-750">
                  <td className="px-4 py-3 text-sm">
                    {formatToEST(event.eventTimestamp)}
                  </td>
                  <td className="px-4 py-3 font-mono text-blue-400">{event.barcodeValue}</td>
                  <td className="px-4 py-3">{event.operatorName}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        event.eventType === 'Success'
                          ? 'bg-green-900 text-green-200'
                          : event.eventType === 'WrongItem'
                          ? 'bg-red-900 text-red-200'
                          : 'bg-yellow-900 text-yellow-200'
                      }`}
                    >
                      {event.eventType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">{event.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-gray-400">No pick events recorded yet.</div>
        )}
      </div>

      {/* Inventory Picker Modal (shared by Reassign and Add) */}
      {pickerMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-700 flex flex-col">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold mb-2">{pickerMode === 'add' ? 'Add Item' : 'Reassign Item'}</h2>
              <p className="text-sm text-gray-400">
                {pickerMode === 'add'
                  ? 'Search for an available inventory item to add to this order.'
                  : 'Original item is broken or missing? Search for a replacement item below.'}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Search and Filters */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Search by Barcode</label>
                  <input
                    type="text"
                    value={searchBarcode}
                    onChange={(e) => setSearchBarcode(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                    placeholder="Search barcode..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Location</label>
                    <select
                      value={filterLocation}
                      onChange={(e) => setFilterLocation(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                    >
                      <option value="">All Locations</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Sort ID 1</label>
                    <input
                      type="text"
                      value={filterSortId1}
                      onChange={(e) => setFilterSortId1(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                      placeholder="Filter by Sort ID 1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Sort ID 2</label>
                    <input
                      type="text"
                      value={filterSortId2}
                      onChange={(e) => setFilterSortId2(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                      placeholder="Filter by Sort ID 2"
                    />
                  </div>
                </div>

                <button
                  onClick={loadAvailableItems}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  Search Items
                </button>
              </div>

              {/* Selected Item */}
              {replacementBarcode && selectedReplacementId && (
                <div className="bg-green-900 bg-opacity-20 border border-green-700 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-400">{pickerMode === 'add' ? 'Selected Item:' : 'Selected Replacement:'}</p>
                      <p className="font-mono text-lg text-green-400">
                        ID {selectedReplacementId} - {replacementBarcode}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setReplacementBarcode('');
                        setSelectedReplacementId(null);
                      }}
                      className="text-gray-400 hover:text-white"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {/* Available Items List */}
              {isLoadingItems ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  <p className="mt-2 text-gray-400">Loading items...</p>
                </div>
              ) : (
                <div>
                  {(() => {
                    // Get all inventory item IDs already in this order
                    const orderInventoryItemIds = new Set(order?.items.map(item => item.inventoryItemId) ?? []);
                    const filteredItems = availableItems.filter(item =>
                      // Exclude any item already in this order
                      !orderInventoryItemIds.has(item.id) &&
                      // Apply search filter
                      (!searchBarcode || item.barcodeValue.toLowerCase().includes(searchBarcode.toLowerCase()))
                    );

                    return (
                      <>
                        <h3 className="text-sm font-medium mb-2">
                          Available Items ({filteredItems.length})
                        </h3>
                        <div className="bg-gray-750 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-700 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left w-10"></th>
                                <th className="px-4 py-2 text-left">ID</th>
                                <th className="px-4 py-2 text-left">Barcode</th>
                                <th className="px-4 py-2 text-left">Location</th>
                                <th className="px-4 py-2 text-left">Sort ID 1</th>
                                <th className="px-4 py-2 text-left">Sort ID 2</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {filteredItems.map((item) => (
                            <tr
                              key={item.id}
                              onClick={() => handleSelectItem(item.id, item.barcodeValue)}
                              className={`cursor-pointer hover:bg-gray-700 ${
                                selectedReplacementId === item.id ? 'bg-green-900 bg-opacity-20' : ''
                              }`}
                            >
                              <td className="px-4 py-2">
                                <input
                                  type="radio"
                                  name="replacement-item"
                                  checked={selectedReplacementId === item.id}
                                  onChange={() => handleSelectItem(item.id, item.barcodeValue)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 cursor-pointer accent-blue-600"
                                />
                              </td>
                              <td className="px-4 py-2 text-gray-400">{item.id}</td>
                              <td className="px-4 py-2 font-mono text-blue-400">{item.barcodeValue}</td>
                              <td className="px-4 py-2">{item.locationCode || '-'}</td>
                              <td className="px-4 py-2">{item.sortId1 || '-'}</td>
                              <td className="px-4 py-2">{item.sortId2 || '-'}</td>
                            </tr>
                              ))}
                            </tbody>
                          </table>
                          {filteredItems.length === 0 && (
                            <div className="text-center py-8 text-gray-400">
                              No items found. Adjust filters and search again.
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-700 flex gap-2 justify-end">
              <button
                onClick={handleClosePicker}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitPicker}
                disabled={!selectedReplacementId}
                data-testid="picker-submit-button"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pickerMode === 'add' ? 'Add Item' : 'Reassign Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Item Modal */}
      <InventoryItemModal
        itemId={selectedInventoryItemId}
        onClose={() => setSelectedInventoryItemId(null)}
      />

      {/* Dialog */}
      <Dialog {...dialog} onClose={closeDialog} />
    </div>
  );
}
